import React from "react";
import { Box, Button } from "@mui/material";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import { useNavigate } from "react-router-dom";

import { loadCurrentSession, SignIn } from "src/handlers/Account";
import {
  ensureActiveProjectSession,
  getActiveProjectId,
  getActiveProjectName
} from "src/handlers/Projects";
import ProjectSwitcher from "src/components/ProjectSwitcher";
import SaveStatusChip from "src/components/SaveStatusChip";
import HeaderUserbox from "../HeaderUserbox";

function getStorageState() {
  return {
    isSignedIn: Boolean(localStorage.getItem("sessioninfo")),
    activeProjectId: getActiveProjectId(),
    activeProjectName: getActiveProjectName()
  };
}

function HeaderButtons() {
  const navigate = useNavigate();
  const [authLoading, setAuthLoading] = React.useState(true);
  const [storageState, setStorageState] = React.useState(getStorageState);

  const refreshStorageState = React.useCallback(() => {
    setStorageState(getStorageState());
  }, []);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await loadCurrentSession();
        if (!getActiveProjectId()) {
          await ensureActiveProjectSession();
        }
      } catch (error) {
        console.error("Failed to bootstrap header state", error);
      } finally {
        if (mounted) {
          refreshStorageState();
          setAuthLoading(false);
        }
      }
    })();

    const handleFocus = async () => {
      await loadCurrentSession();
      if (!getActiveProjectId()) {
        await ensureActiveProjectSession();
      }
      refreshStorageState();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("storage", refreshStorageState);

    return () => {
      mounted = false;
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("storage", refreshStorageState);
    };
  }, [refreshStorageState]);

  const renderAuthSection = () => {
    if (authLoading) {
      return (
        <Button sx={{ ml: 2 }} variant="text" disabled>
          Loading...
        </Button>
      );
    }

    if (!storageState.isSignedIn) {
      return (
        <Button onClick={SignIn} sx={{ ml: 2 }} variant="text" size="small">
          Sign in
        </Button>
      );
    }

    return <HeaderUserbox onSignedOut={refreshStorageState} />;
  };

  return (
    <Box sx={{ mr: 1 }}>
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Box sx={{ mx: 0.5 }} component="span" display="flex" alignItems="center" gap={1.25}>
          {/* Phase 2.1 — save/sync status chip */}
          <SaveStatusChip />

          {/* Phase 2.1 — project chip now doubles as a switcher, with a
              "Local project" fallback when the user is signed out. */}
          <ProjectSwitcher onNavigate={refreshStorageState} />

          {storageState.isSignedIn ? (
            <Button
              startIcon={<SpaceDashboardRoundedIcon />}
              onClick={() => navigate("/projects")}
              variant="contained"
            >
              Projects
            </Button>
          ) : null}

          {renderAuthSection()}
        </Box>
      </Box>
    </Box>
  );
}

export default HeaderButtons;
